/**
 * The render-target layout every effect material uses. The scene pass writes normals, velocity
 * and diffuse colour beside the colour output (for ambient occlusion, TAAU and SSGI); an effect
 * must leave those buffers exactly as the opaque scene left them. Writing zeros is not enough:
 * three's MRT node defaults every extra target to no blending, so a flash or spark would punch
 * holes in the normal and velocity buffers wherever it passed the depth test (found in Phase 8;
 * DECISIONS.md #82 corrected). A custom blend that keeps the destination (source × 0 +
 * destination × 1) does the right thing on WebGPU and on WebGL 2 alike.
 */
import { BlendMode, CustomBlending, OneFactor, ZeroFactor } from 'three/webgpu';
import { mrt, vec2, vec3, vec4 } from 'three/tsl';

export type EffectMrt = ReturnType<typeof mrt>;

const GBUFFER_TARGETS = ['normal', 'velocity', 'diffuseColor'] as const;

function keepDestination(): BlendMode {
  const blend = new BlendMode(CustomBlending);
  blend.blendSrc = ZeroFactor;
  blend.blendDst = OneFactor;
  blend.blendSrcAlpha = ZeroFactor;
  blend.blendDstAlpha = OneFactor;
  return blend;
}

/** An MRT node whose G-buffer targets are untouched by the draw. */
export function effectMrt(): EffectMrt {
  const node = mrt({ normal: vec3(0), velocity: vec2(0), diffuseColor: vec4(0) });
  const keep = keepDestination();
  for (const name of GBUFFER_TARGETS) node.setBlendMode(name, keep);
  return node;
}
